import { NextApiRequest, NextApiResponse } from 'next'

import { STAGING_HOST } from '../constants'

// sync with `network-graphql-builder` in .juscli
const LOCAL_GRAPHQL = 'http://localhost:4500/graphql'

// `gcloud auth print-access-token` takes ~0.5s, so cache the token in-process.
// Access tokens last ~1h; cache for 50 min, then re-fetch lazily on the next call.
const GCLOUD_TOKEN_TTL_MS = 50 * 60 * 1000
let cachedGcloudToken: { value: string; expiresAt: number } | undefined

/** Test-only: clears the in-process gcloud access-token cache. */
export function resetGcloudTokenCache() {
  cachedGcloudToken = undefined
}

/**
 * Obtains a Google Cloud access token from the developer's local `gcloud` CLI so
 * the dev proxy can forward `Authorization: Bearer <token>` to network-graphql.
 *
 * Only invoked on local dev — the caller gates the gcloud branch to
 * `NODE_ENV === 'development'`, so this never spawns `gcloud` on CI or staging.
 * Returns `undefined` (and warns) when gcloud is missing, unauthenticated, or
 * returns unusable output, letting the request fall back to cookie-based auth
 * instead of failing.
 */
async function getGcloudAccessToken(): Promise<string | undefined> {
  if (cachedGcloudToken && cachedGcloudToken.expiresAt > Date.now()) {
    return cachedGcloudToken.value
  }

  try {
    // `child_process` is a Node core module with no browser equivalent. This file
    // is reachable from the client bundle through the core-next barrel, so the
    // webpackIgnore hint keeps webpack from trying to resolve it client-side —
    // graphqlMiddleware only ever runs server-side, so the import never executes
    // in the browser.
    const { execFile } = await import(/* webpackIgnore: true */ 'child_process')
    // Bound the call: `print-access-token` may hit the network to refresh, and
    // without a timeout a stalled gcloud (network partition, hung credential
    // helper, prompt waiting on stdin) would hang the request forever.
    const token = await new Promise<string>((resolve, reject) => {
      execFile(
        'gcloud',
        ['auth', 'print-access-token'],
        { timeout: 5000 },
        (error, stdout, stderr) => {
          if (error) {
            const detail = stderr ? String(stderr).trim() : ''
            reject(detail ? new Error(`${error.message} (${detail})`) : error)
          } else {
            resolve(String(stdout).trim())
          }
        }
      )
    })

    // gcloud prints only the token to stdout (notices/errors go to stderr), but
    // guard against empty or junk output (a banner/update notice): a real access
    // token is a single whitespace-free string. Caching junk here would send it
    // as `Bearer <junk>` on every request for the whole TTL, so warn and bail.
    if (!token || /\s/.test(token)) {
      console.warn(
        `[core-next] "gcloud auth print-access-token" returned no usable token ` +
          `(try "gcloud auth login")`
      )
      return undefined
    }

    cachedGcloudToken = { value: token, expiresAt: Date.now() + GCLOUD_TOKEN_TTL_MS }
    return token
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    console.warn(
      `[core-next] Could not obtain a gcloud access token for GraphQL auth ` +
        `(try "gcloud auth login"): ${message}`
    )
    return undefined
  }
}

export async function graphqlMiddleware(req: NextApiRequest, res: NextApiResponse) {
  const host = req.headers.host
  const isStagingHost = host?.includes(STAGING_HOST)
  const isCI = !!process.env.CI

  if (process.env.NODE_ENV !== 'development' && !isCI && !isStagingHost) {
    return res.status(404).send(null)
  }

  const url = process.env.NETWORK_GRAPHQL_PLAYGROUND_URL || LOCAL_GRAPHQL
  const urlWithBatch = req.url?.endsWith('/apollo-batch') ? `${url}/apollo-batch` : url

  const clientName = req?.headers['apollographql-client-name'] as string

  const headers: HeadersInit = {
    Cookie: req?.headers.cookie || '',
    'User-Agent': req?.headers['user-agent'] || '',
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(clientName && { 'apollographql-client-name': clientName }),
  }
  const cfConnectingIpHeader = req?.headers['cf-connecting-ip']
  if (cfConnectingIpHeader) {
    headers['CF-Connecting-IP'] = Array.isArray(cfConnectingIpHeader)
      ? cfConnectingIpHeader.join('; ')
      : cfConnectingIpHeader
  }

  // Authorization precedence: an inbound header (e.g. forwarded from a B2B SSR
  // request) wins; then an explicit SA_ACCESS_TOKEN; otherwise a short-lived
  // token from the developer's gcloud session so the local network-graphql can
  // authenticate the caller (e.g. for group-gated mutations).
  //
  // The gcloud fallback is gated to `development` on purpose: this middleware
  // also runs on CI and staging (guard above), and there we must not shell out
  // to `gcloud` and forward whatever session token it finds.
  if (req?.headers.authorization) {
    headers.Authorization = req.headers.authorization
  } else if (process.env.SA_ACCESS_TOKEN) {
    headers.Authorization = `Bearer ${process.env.SA_ACCESS_TOKEN}`
  } else if (process.env.NODE_ENV === 'development') {
    const gcloudToken = await getGcloudAccessToken()
    if (gcloudToken) {
      headers.Authorization = `Bearer ${gcloudToken}`
    }
  }

  let response: Response
  try {
    response = await fetch(urlWithBatch, {
      method: req.method,
      body: JSON.stringify(req.body),
      headers,
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return res.json({
      data: null,
      errors: [
        {
          message: `[core-next] Failed to connect to GraphQL at ${urlWithBatch}: ${message}. Run "jus configure" to set up the server.`,
        },
      ],
    })
  }

  const setCookies: string[] = []
  response.headers.forEach((value, key) => {
    if (key.toLowerCase() === 'set-cookie') setCookies.push(value)
  })
  res.setHeader('Set-Cookie', setCookies)

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    return res.json({
      data: null,
      errors: [
        {
          message: `[core-next] Unexpected response from GraphQL at ${urlWithBatch}: "${contentType}" (HTTP ${response.status}). The server may be down or misconfigured.`,
        },
      ],
    })
  }

  const data = await response.json()
  return res.json(data)
}
